<template>
  <div>
    <paper-modal-container :is-visible="true" title="결제 처리 중" :hide-close-button="true" @close="goMyAds">
      <div class="success-wrapper">
        <div v-if="state === 'processing'" class="state-block">
          <div class="spinner"></div>
          <p class="state-title">결제 정보를 확인하고 있습니다...</p>
          <p class="state-desc">잠시만 기다려 주세요. 이 화면을 닫지 마세요.</p>
        </div>

        <div v-else-if="state === 'success'" class="state-block">
          <div class="success-icon">✓</div>
          <p class="state-title state-success">
            {{ isPreviewMode ? "토스 카드 등록 테스트 완료" : "광고 신청이 완료되었습니다" }}
          </p>
          <p class="state-desc">
            <template v-if="isPreviewMode">
              토스에서 카드 등록(빌링키 발급)까지 확인되었습니다.
              서버 API 연동 후 실제 결제·심사·광고 게재가 진행됩니다.
            </template>
            <template v-else>
              첫 결제가 완료되었으며 관리자 심사 후 홈 하단·상세 페이지에 광고가 노출됩니다.
              (영업일 기준 1~2일 소요)
            </template>
          </p>
          <div class="info-box">
            <div class="info-row">
              <span class="info-label">캠페인 번호</span>
              <span class="info-value">{{ campaignId || "-" }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">상태</span>
              <span class="info-value status-pill">{{ isPreviewMode ? "미리보기" : "심사 대기" }}</span>
            </div>
            <div v-if="authKey" class="info-row">
              <span class="info-label">authKey</span>
              <span class="info-value info-value-mono">{{ authKeyPreview }}</span>
            </div>
          </div>
          <button type="button" class="primary-btn" @click="goCampaignDetail">내 광고로 이동</button>
        </div>

        <div v-else-if="state === 'error'" class="state-block">
          <div class="error-icon">!</div>
          <p class="state-title state-error">결제 처리 중 오류가 발생했습니다</p>
          <p class="state-desc">{{ errorMessage }}</p>
          <p v-if="campaignId" class="state-desc-sub">캠페인 번호: {{ campaignId }}</p>
          <p class="state-desc-sub">
            카드는 등록되었으나 광고 등록이 실패했을 수 있습니다.<br />
            아래 「내 광고」 화면에서 상태를 확인하거나 카카오톡 「링꾹」 채널로 문의해 주세요.
          </p>
          <div class="action-row">
            <button type="button" class="secondary-btn" @click="retry">다시 시도</button>
            <button type="button" class="primary-btn" @click="goMyAds">내 광고로 이동</button>
          </div>
        </div>
      </div>
    </paper-modal-container>
  </div>
</template>

<script>
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";
import { confirmAdBillingIssue } from "@/utils/adApplication";

const PENDING_KEY_PREFIX = "adBillingPending:";

export default {
  name: "AdPaymentSuccess",
  components: { PaperModalContainer },
  data() {
    return {
      state: "processing",
      campaignId: "",
      customerKey: "",
      authKey: "",
      errorMessage: "",
      isPreviewMode: false,
    };
  },
  computed: {
    authKeyPreview() {
      if (!this.authKey) return "";
      if (this.authKey.length <= 16) return this.authKey;
      return `${this.authKey.slice(0, 8)}…${this.authKey.slice(-4)}`;
    },
  },
  mounted() {
    this.processBilling();
  },
  methods: {
    async processBilling() {
      try {
        const query = this.$route.query;
        this.customerKey = String(query.customerKey || "");
        this.authKey = String(query.authKey || "");

        if (!this.customerKey || !this.authKey) {
          throw new Error("결제 결과 정보가 없습니다.");
        }

        const pendingKey = `${PENDING_KEY_PREFIX}${this.customerKey}`;
        const raw = sessionStorage.getItem(pendingKey);
        if (!raw) throw new Error("결제 진행 정보가 만료되었습니다. 신청을 다시 진행해주세요.");
        const pending = JSON.parse(raw);
        this.campaignId = pending.campaignId || "";
        this.isPreviewMode = !!pending.isLocalPreview;

        if (!this.campaignId) throw new Error("캠페인 정보를 찾을 수 없습니다.");

        if (this.isPreviewMode) {
          sessionStorage.removeItem(pendingKey);
          this.state = "success";
          return;
        }

        try {
          await confirmAdBillingIssue({
            campaignId: this.campaignId,
            customerKey: this.customerKey,
            authKey: this.authKey,
          });
        } catch (e) {
          console.error("광고 빌링 발급 API 실패", e);
          throw e;
        }

        sessionStorage.removeItem(pendingKey);
        this.state = "success";
      } catch (e) {
        console.error("광고 빌링 발급 실패", e);
        this.errorMessage = e?.response?.data?.message || e?.message || "결제 처리 중 오류가 발생했습니다.";
        this.state = "error";
      }
    },
    goCampaignDetail() {
      if (this.campaignId) {
        this.$router.push(`/my-ads/${this.campaignId}`);
      } else {
        this.$router.push("/my-ads");
      }
    },
    goMyAds() {
      this.$router.push("/my-ads");
    },
    retry() {
      if (this.campaignId) {
        this.$router.push(`/ads/apply/${this.campaignId}/checkout`);
      } else {
        this.$router.push("/ads/apply");
      }
    },
  },
};
</script>

<style scoped>
.success-wrapper {
  padding: 24px 0;
  max-width: 600px;
  margin: 0 auto;
}

.state-block {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #66bdff;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.success-icon,
.error-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-family: "NanumSquareRoundEB", sans-serif;
  margin: 0 auto 20px;
  color: #fff;
}

.success-icon {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.error-icon {
  background: linear-gradient(135deg, #f56565 0%, #c53030 100%);
}

.state-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  color: #2d3748;
  margin: 0 0 8px 0;
}

.state-title.state-success { color: #2f855a; }
.state-title.state-error { color: #c53030; }

.state-desc {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 8px 0;
  line-height: 1.6;
}

.state-desc-sub {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  color: #718096;
  margin: 0 0 4px 0;
  line-height: 1.7;
}

.info-box {
  margin: 24px auto;
  max-width: 380px;
  text-align: left;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px 20px;
}

.info-row {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  flex: 0 0 100px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #495057;
}

.info-value {
  flex: 1;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #212529;
  overflow-wrap: anywhere;
}

.status-pill {
  display: inline-block;
  background: #fff3bf;
  color: #5c3c00;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
}

.info-value-mono {
  font-size: 11px;
  word-break: break-all;
}

.action-row {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
}

.primary-btn {
  margin-top: 16px;
  padding: 12px 28px;
  background: #66bdff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.primary-btn:hover { background: #4da3ff; }

.secondary-btn {
  margin-top: 16px;
  padding: 12px 22px;
  background: #f1f3f5;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

@media (max-width: 480px) {
  .action-row {
    flex-direction: column;
  }
  .primary-btn,
  .secondary-btn {
    width: 100%;
  }
}
</style>
